import { EntityTypes, Timestamps } from './shared.types';

export interface SalesReport extends Timestamps {
  gross_sales: number;
  refunds: number;
  discounts: number;
  cost_of_products: number;
  expenses: number;
  entity_type: EntityTypes;
  sale_receipt_count: number;
  refund_receipt_count: number;
  expense_count: number;
  source_entity_id: string;
  source_entity_type: EntityTypes;
  version: number;
  merchant_id: string;
  store_id: string;
  shift_id?: string; // TODO: Make Optional // Shift during which the sales report was created
}

export interface StoreSalesReport extends SalesReport {
  store_id: string;
}

export interface EmployeeSalesReport extends SalesReport {
  employee_id: string;
  employee_name: string;
}

export interface ProductSalesReport extends SalesReport {
  product_id: string;
  quantity: number;
  product_name: string;
  sku: string;
}

export interface SalesReportDBItem {
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  GSI2PK: string;
  GSI2SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

export interface StoreSalesReportDBItem
  extends StoreSalesReport,
    SalesReportDBItem {}

export interface StoreSalesReportDBItemSnapshot
  extends AggregateStoreSalesReport {
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  /**
   * True on snapshots that include their own event (written since the
   * snapshot fix). Optional for backwards compatibility; missing = an older
   * snapshot that may under-count, so it is never built on.
   */
  includes_own_event?: boolean;
}

export interface EmployeeSalesReportDBItem
  extends EmployeeSalesReport,
    SalesReportDBItem {}

export interface EmployeeSalesReportDBItemSnapshot
  extends AggregateEmployeeSalesReport {
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  /**
   * True on snapshots that include their own event (written since the
   * snapshot fix). Optional for backwards compatibility; missing = an older
   * snapshot that may under-count, so it is never built on.
   */
  includes_own_event?: boolean;
}

export interface ProductSalesReportDBItem
  extends ProductSalesReport,
    SalesReportDBItem {}

export interface ProductSalesReportDBItemSnapshot
  extends AggregateProductSalesReport {
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  /**
   * True on snapshots that include their own event (written since the
   * snapshot fix). Optional for backwards compatibility; missing = an older
   * snapshot that may under-count, so it is never built on.
   */
  includes_own_event?: boolean;
}

export interface SalesReportExtensions {
  net_sales: number;
  gross_profit: number;
  net_profit: number;
  gross_margin: number;
  net_margin: number;
}

export interface AggregateStoreSalesReport
  extends StoreSalesReport,
    SalesReportExtensions {}

export interface AggregateEmployeeSalesReport
  extends EmployeeSalesReport,
    SalesReportExtensions {}

export interface AggregateProductSalesReport
  extends ProductSalesReport,
    SalesReportExtensions {}

export interface AggregateSalesReport {
  store: AggregateStoreSalesReport;
  employees: AggregateEmployeeSalesReport[];
  products: AggregateProductSalesReport[];
}

export interface GetSalesReport {
  merchant_id: string;
  store_id: string;
  products?: string[];
  from?: string;
  to?: string;
  date?: string;
  by_date?: boolean;
  by_date_range?: boolean;
  by_products_and_date?: boolean;
  by_products_and_date_range?: boolean;
  /**
   * Optional for backwards compatibility; missing = true.
   * Set to false to skip the per-employee reports (saves one query per
   * employee and the users lookup).
   */
  include_employees?: boolean;
  /**
   * Optional for backwards compatibility; missing = true.
   * Set to false to skip the per-product reports, the heaviest part of a
   * date-range report (one row per receipt line is scanned).
   */
  include_products?: boolean;
}

export interface GetReportEvents {
  merchant_id: string;
  by_source_entity?: boolean;
  source_entity_type?: EntityTypes;
  source_entity_id?: string;
}

export interface ReportEventsPage {
  events: ProductSalesReport[] | StoreSalesReport[] | EmployeeSalesReport[];
  count: number;
  cursor?: string;
}

export interface GetInventoryValue {
  merchant_id: string;
  store_id: string;
  date?: string;
}

export interface GetProductsInventoryValue {
  merchant_id: string;
  store_id: string;
  date: string;
  cursor?: string;
  limit?: number;
}

export interface ProductInventoryValue {
  price: number;
  cost: number;
  inventory_value: number;
  retail_value: number;
  potential_profit: number;
  margin: number;
  created_at: string;
  product_id: string;
  product_name: string;
  store_id: string;
  in_stock: number;
  merchant_id: string;
  /**
   * Stock received but waiting for price approval, and its value at cost.
   * Not in `in_stock` or `inventory_value`. Optional for backwards
   * compatibility; missing = 0. Set only when non-zero.
   */
  pending_quantity?: number;
  pending_value?: number;
  /**
   * Blocked stock (extra goods awaiting validation, damaged returns) and its
   * value at cost. Not in `in_stock` or `inventory_value`. Optional for
   * backwards compatibility; missing = 0. Set only when non-zero.
   */
  blocked_quantity?: number;
  blocked_value?: number;
}

export interface ProductInventoryValuePage {
  inventory_values: ProductInventoryValue[];
  count: number;
  cursor?: string;
}

export interface StoreInventoryValue {
  store_id: string;
  merchant_id: string;
  inventory_value: number;
  potential_profit: number;
  retail_value: number;
  margin: number;
  created_at: string;
  /**
   * Value at cost of stock waiting for price approval, outside
   * `inventory_value`. Optional for backwards compatibility; missing = 0.
   * Set only when non-zero.
   */
  pending_value?: number;
  /**
   * Value at cost of blocked stock, outside `inventory_value`. Optional for
   * backwards compatibility; missing = 0. Set only when non-zero.
   */
  blocked_value?: number;
}

export interface MerchantInventoryValue {
  merchant_id: string;
  merchant_name: string;
  inventory_value: number;
  potential_profit: number;
  retail_value: number;
  margin: number;
  created_at: string;
}

export interface StoreValueSnapshotDBItem extends StoreInventoryValue {
  PK: string; // MERCHANT#<merchant_id>#STORE_VALUE_SNAPSHOT#<store_id>
  SK: string; // STORE_VALUE_SNAPSHOT#<datetime>
  EntityIndexPK: string; //STORE_VALUE_SNAPSHOT
  EntityIndexSK: string; //MERCHNAT#<merchant_id>#STORE#<store_id>#DATE#<datetime>
}

export interface GetStoreInventoryValueDateRange {
  merchant_id: string;
  store_id: string;
  from: string;
  to: string;
  /**
   * Optional for backwards compatibility; missing = every snapshot in the
   * range. The most points to return (2 or more). When the full series is
   * longer, it is sampled instead: the start value, then the value at the
   * end of each interval (minutes to weeks, the finest that fits), ending
   * with the latest value.
   */
  max_points?: number;
}

export interface StoreInventoryValueDateRangePage {
  inventory_values: StoreInventoryValue[];
  count: number;
}

export interface UpdateStoreInventorySQSMessage {
  store_id: string;
  merchant_id: string;
}

/**
 * SQS message asking the reports service to recompute a store's inventory
 * value snapshot. Sent whenever anything that affects the store's inventory
 * value changes (stock movement, cost/price edit, product delete).
 * The worker recomputes the snapshot from live inventory, so the message is
 * an idempotent trigger — duplicates and lost deltas cannot skew the total.
 */
export interface InventoryValueChangeSQSMessage {
  store_id: string;
  merchant_id: string;
  /** @deprecated informational only — the worker recomputes from live inventory */
  inventory_value_delta?: number;
  /** @deprecated informational only — the worker recomputes from live inventory */
  retail_value_delta?: number;
}

/**
 * Interval types for date-range segmentation
 */
export enum SalesReportInterval {
  HOURLY = 'hourly',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  ANNUALLY = 'annually',
  NONE = 'none',
}

export interface GetSalesReportByDateRangeIntervalOptions {
  entityType: EntityTypes;
  entityId: string;
  from: string;
  to: string;
  interval?: SalesReportInterval;
  is_cumulative?: boolean;
}

export interface IntervalAggregateReport<T> {
  interval_start: string;
  interval_end: string;
  aggregated_report: T;
}

/**
 * Trigger source of an inventory value CSV export. `manual` covers the
 * inventory-value page download button; `scheduled` covers the nightly
 * per-store generation.
 */
export type InventoryValueExportTrigger = 'manual' | 'scheduled';

export interface GenerateInventoryValueExportRequest {
  /**
   * ISO 8601 date shown on the inventory-value page when the export was
   * requested. Recorded as S3 metadata; the CSV rows themselves always
   * reflect live inventory at current cost/price.
   */
  date?: string;
  requested_by?: string;
}

export interface InventoryValueExportRecord {
  key: string;
  file_name: string;
  job_id: string;
  requested_at: string;
  triggered_by: InventoryValueExportTrigger;
  download_url: string;
  requested_by?: string;
  size_in_bytes?: number;
  last_modified?: string;
  /**
   * Set when the signed inventory-count PDF generated alongside the CSV
   * exists in S3. Absent for exports created before PDF generation shipped
   * or when the PDF could not be generated.
   */
  pdf_key?: string;
  pdf_download_url?: string;
  pdf_size_in_bytes?: number;
}

/**
 * Response of the manual export trigger. Generation runs asynchronously on
 * the export queue; the files appear on the Downloads page when the worker
 * finishes (CSV plus the inventory-count PDFs).
 */
export interface GenerateInventoryValueExportJobResponse {
  job_id: string;
  requested_at: string;
  triggered_by: InventoryValueExportTrigger;
  /** Predicted S3 key of the CSV the queued job will write. */
  csv_key: string;
  requested_by?: string;
}

export interface InventoryValueExportsResponse {
  exports: InventoryValueExportRecord[];
  next_cursor?: string;
}

/**
 * SQS message asking the reports service to generate an inventory value CSV
 * export for a store. Sent by the nightly cron for every merchant/store and
 * consumed by the inventory value export queue worker.
 */
export interface InventoryValueExportSQSMessage {
  job_id: string;
  merchant_id: string;
  store_id: string;
  triggered_by: InventoryValueExportTrigger;
  requested_at: string;
  date: string;
  requested_by?: string;
}
